// The functions a formula can call, as Google Sheets has them: maths, counting, conditions, lookups, text, dates and a
// loan's payment. Each says how it is called and what it does in a few words, for the formula box's suggestions.
// A function throws a SheetErr for a wrong argument; the engine shows it in the cell.

import { Arr, RangeV, atIn, cmp, criterion, dims, each, grid, isBlock, numText, same, scalar, sig15, toBool, toNum, toStr, wildcard, type Budget, type TabView, type Val } from "./core";
import { formatCode } from "./format";
import { linkOf } from "./links";
import { MAX_COLS, MAX_ROWS } from "./addr";
import { err, isErr, readInput, serialOf, textNumber, weekdayOf, ymd, type Scalar, SheetErr } from "./values";

export interface Args {
  n: number;
  /** whether argument i was given (not left empty, as in IF(A1,,2)) */
  has(i: number): boolean;
  val(i: number): Val;
  sc(i: number): Scalar;
  num(i: number, dflt?: number): number;
  str(i: number, dflt?: string): string;
  bool(i: number, dflt?: boolean): boolean;
  dmy: boolean;
  /** now, as a day number with its time, in the user's time zone */
  now: number;
  budget: Budget;
  here: { tab: TabView; r: number; c: number };
  /** makes the cell a link to an address (HYPERLINK) */
  setLink?: (url: string) => void;
  /** a reference written as text, as the cells it names (INDIRECT) */
  ref?: (text: string) => Val;
}

export interface FnSpec {
  min: number;
  max: number;
  sig: string;
  about: string;
  run: (a: Args) => Val;
}

const fail = (code: ConstructorParameters<typeof SheetErr>[0], msg = ""): never => {
  throw new SheetErr(code, msg);
};

const ok = (v: number | SheetErr): number => {
  if (isErr(v)) throw v;
  if (!Number.isFinite(v)) fail("#NUM!", "The result is too large.");
  return v;
};

/** Every number in the arguments: numbers in blocks (text and TRUE or FALSE there skipped), and numbers given directly. */
function nums(a: Args, from = 0): number[] {
  const out: number[] = [];
  for (let i = from; i < a.n; i++) {
    if (!a.has(i)) continue;
    for (const [v, inBlock] of each(a.val(i), a.budget)) {
      if (isErr(v)) throw v;
      if (inBlock) {
        if (typeof v === "number") out.push(v);
      } else out.push(ok(toNum(v, a.dmy)));
    }
  }
  return out;
}

function sum(xs: number[]): number {
  let s = 0;
  for (const x of xs) s += x;
  return s;
}

function round(n: number, d: number, how: "half" | "up" | "down"): number {
  const p = Math.pow(10, Math.trunc(d));
  // to 15 significant digits first, as Sheets keeps numbers: 2.675 is 2.675, and rounds to 2.68
  const x = sig15(Math.abs(n) * p);
  // a hair over to cover binary fractions: 1.005 rounds to 1.01, as a person expects
  const eps = 1e-9 * Math.max(1, x);
  const r = how === "half" ? Math.floor(x + 0.5 + eps) : how === "up" ? Math.ceil(x - eps) : Math.floor(x + eps);
  return (Math.sign(n) * r) / p;
}

/** The pairs of (block, criterion) after the first from, for SUMIFS and the like; every block the size of the first. */
function critPairs(a: Args, from: number, shape: { h: number; w: number }): { v: Val; test: (x: Scalar) => boolean }[] {
  const out: { v: Val; test: (x: Scalar) => boolean }[] = [];
  for (let i = from; i + 1 < a.n; i += 2) {
    const v = a.val(i);
    const d = dims(v);
    if (d.h !== shape.h || d.w !== shape.w) fail("#VALUE!", "Each range in the criteria is the same size.");
    out.push({ v, test: criterion(a.sc(i + 1), a.dmy) });
  }
  return out;
}

/** Each cell of a block that meets every criterion, by its place in the block. */
function* matching(shape: { h: number; w: number }, pairs: { v: Val; test: (x: Scalar) => boolean }[], budget: Budget): Generator<[number, number]> {
  budget.spend(shape.h * shape.w * Math.max(1, pairs.length));
  for (let i = 0; i < shape.h; i++)
    for (let j = 0; j < shape.w; j++) {
      let hit = true;
      for (const p of pairs)
        if (!p.test(atIn(p.v, i, j))) {
          hit = false;
          break;
        }
      if (hit) yield [i, j];
    }
}

function lookupIndex(keys: Scalar[], key: Scalar, mode: "exact" | "below" | "above", wild: boolean, last = false): number {
  if (mode === "exact") {
    if (last) {
      for (let i = keys.length - 1; i >= 0; i--) if (same(keys[i], key, wild)) return i;
      return -1;
    }
    return keys.findIndex((k) => same(k, key, wild));
  }
  let best = -1;
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (k === null || typeof k !== typeof key) continue;
    const d = cmp(k, key);
    if (d === 0) return i;
    if (mode === "below" ? d < 0 : d > 0) {
      if (best < 0 || (mode === "below" ? cmp(k, keys[best]) > 0 : cmp(k, keys[best]) < 0)) best = i;
    }
  }
  return best;
}

/** Sorted-data lookup as VLOOKUP and MATCH(…,1) do it: the last key at or under the one sought. */
function sortedIndex(keys: Scalar[], key: Scalar, desc = false): number {
  let found = -1;
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (k === null) continue;
    if (typeof k !== typeof key) continue;
    const d = cmp(k, key);
    if (desc ? d >= 0 : d <= 0) found = i;
    else break;
  }
  return found;
}

function col(v: Val, j: number): Scalar[] {
  const d = dims(v);
  const out: Scalar[] = [];
  for (let i = 0; i < d.h; i++) out.push(atIn(v, i, j));
  return out;
}

function row(v: Val, i: number): Scalar[] {
  const d = dims(v);
  const out: Scalar[] = [];
  for (let j = 0; j < d.w; j++) out.push(atIn(v, i, j));
  return out;
}

function line(v: Val): Scalar[] {
  const d = dims(v);
  if (d.h !== 1 && d.w !== 1) fail("#N/A", "The range to look in is one row or one column.");
  return d.w === 1 ? col(v, 0) : row(v, 0);
}

function dateArg(a: Args, i: number): number {
  const v = a.sc(i);
  if (isErr(v)) throw v;
  if (typeof v === "string") {
    const n = textNumber(v, a.dmy);
    if (n === null) fail("#VALUE!", `"${v}" is not a date.`);
    return Math.floor(n!);
  }
  return Math.floor(ok(toNum(v, a.dmy)));
}

function addMonths(serial: number, months: number, end: boolean): number {
  const { y, m, d } = ymd(serial);
  const total = y * 12 + (m - 1) + Math.trunc(months);
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return serialOf(ny, nm, end ? last : Math.min(d, last));
}

const text = (a: Args, i: number, dflt?: string) => a.str(i, dflt);

export const FUNCS: Record<string, FnSpec> = {
  // ------------------------------------------------------------- maths and counting
  SUM: { min: 1, max: 255, sig: "SUM(value1, [value2, …])", about: "Adds numbers.", run: (a) => sum(nums(a)) },
  AVERAGE: {
    min: 1,
    max: 255,
    sig: "AVERAGE(value1, [value2, …])",
    about: "The average of numbers.",
    run: (a) => {
      const xs = nums(a);
      return xs.length ? sum(xs) / xs.length : fail("#DIV/0!", "There are no numbers to average.");
    },
  },
  COUNT: { min: 1, max: 255, sig: "COUNT(value1, [value2, …])", about: "How many numbers.", run: (a) => countWhere(a, (v, inBlock) => typeof v === "number" || (!inBlock && typeof v === "string" && textNumber(v, a.dmy) !== null)) },
  COUNTA: { min: 1, max: 255, sig: "COUNTA(value1, [value2, …])", about: "How many values, of any kind.", run: (a) => countWhere(a, (v) => v !== null) },
  COUNTBLANK: { min: 1, max: 1, sig: "COUNTBLANK(range)", about: "How many empty cells.", run: (a) => countWhere(a, (v) => v === null || v === "") },
  MIN: { min: 1, max: 255, sig: "MIN(value1, [value2, …])", about: "The smallest number.", run: (a) => ((xs) => (xs.length ? Math.min(...xs) : 0))(nums(a)) },
  MAX: { min: 1, max: 255, sig: "MAX(value1, [value2, …])", about: "The largest number.", run: (a) => ((xs) => (xs.length ? Math.max(...xs) : 0))(nums(a)) },
  MEDIAN: {
    min: 1,
    max: 255,
    sig: "MEDIAN(value1, [value2, …])",
    about: "The middle number.",
    run: (a) => {
      const xs = nums(a).sort((x, y) => x - y);
      if (!xs.length) fail("#NUM!", "There are no numbers.");
      const m = xs.length >> 1;
      return xs.length % 2 ? xs[m] : (xs[m - 1] + xs[m]) / 2;
    },
  },
  PRODUCT: { min: 1, max: 255, sig: "PRODUCT(value1, [value2, …])", about: "Multiplies numbers.", run: (a) => nums(a).reduce((p, x) => p * x, 1) },
  SUMPRODUCT: {
    min: 1,
    max: 30,
    sig: "SUMPRODUCT(range1, [range2, …])",
    about: "Multiplies ranges cell by cell, then adds.",
    run: (a) => {
      const blocks = Array.from({ length: a.n }, (_, i) => a.val(i));
      const d = dims(blocks[0]);
      if (blocks.some((b) => dims(b).h !== d.h || dims(b).w !== d.w)) fail("#VALUE!", "Each range is the same size.");
      a.budget.spend(d.h * d.w * blocks.length);
      let s = 0;
      for (let i = 0; i < d.h; i++)
        for (let j = 0; j < d.w; j++) {
          let p = 1;
          for (const b of blocks) {
            const v = atIn(b, i, j);
            if (isErr(v)) throw v;
            p *= typeof v === "number" ? v : 0;
          }
          s += p;
        }
      return s;
    },
  },
  ROUND: { min: 1, max: 2, sig: "ROUND(value, [places])", about: "Rounds to a number of decimals.", run: (a) => round(a.num(0), a.num(1, 0), "half") },
  ROUNDUP: { min: 1, max: 2, sig: "ROUNDUP(value, [places])", about: "Rounds away from zero.", run: (a) => round(a.num(0), a.num(1, 0), "up") },
  ROUNDDOWN: { min: 1, max: 2, sig: "ROUNDDOWN(value, [places])", about: "Rounds toward zero.", run: (a) => round(a.num(0), a.num(1, 0), "down") },
  ABS: { min: 1, max: 1, sig: "ABS(value)", about: "A number without its sign.", run: (a) => Math.abs(a.num(0)) },
  INT: { min: 1, max: 1, sig: "INT(value)", about: "Rounds down to a whole number.", run: (a) => Math.floor(a.num(0)) },
  MOD: {
    min: 2,
    max: 2,
    sig: "MOD(dividend, divisor)",
    about: "The remainder after dividing.",
    run: (a) => {
      const x = a.num(0);
      const d = a.num(1);
      if (d === 0) fail("#DIV/0!", "MOD divides by zero.");
      return x - d * Math.floor(x / d);
    },
  },
  POWER: { min: 2, max: 2, sig: "POWER(base, exponent)", about: "A number raised to a power.", run: (a) => ok(Math.pow(a.num(0), a.num(1))) },
  SQRT: {
    min: 1,
    max: 1,
    sig: "SQRT(value)",
    about: "The square root.",
    run: (a) => {
      const x = a.num(0);
      return x < 0 ? fail("#NUM!", "SQRT of a negative number.") : Math.sqrt(x);
    },
  },
  CEILING: { min: 1, max: 2, sig: "CEILING(value, [factor])", about: "Rounds up to a multiple.", run: (a) => ((x, f) => (f === 0 ? 0 : Math.ceil(x / f - 1e-12) * f))(a.num(0), a.num(1, 1)) },
  FLOOR: { min: 1, max: 2, sig: "FLOOR(value, [factor])", about: "Rounds down to a multiple.", run: (a) => ((x, f) => (f === 0 ? 0 : Math.floor(x / f + 1e-12) * f))(a.num(0), a.num(1, 1)) },
  PI: { min: 0, max: 0, sig: "PI()", about: "The number pi.", run: () => Math.PI },
  RAND: { min: 0, max: 0, sig: "RAND()", about: "A random number from 0 up to 1, new with every change.", run: () => Math.random() },
  RANDBETWEEN: {
    min: 2,
    max: 2,
    sig: "RANDBETWEEN(low, high)",
    about: "A random whole number from low to high, new with every change.",
    run: (a) => {
      const lo = Math.ceil(a.num(0));
      const hi = Math.floor(a.num(1));
      return lo > hi ? fail("#NUM!", "low is more than high.") : lo + Math.floor(Math.random() * (hi - lo + 1));
    },
  },
  STDEV: { min: 1, max: 255, sig: "STDEV(value1, [value2, …])", about: "How spread out numbers are (a sample).", run: (a) => spread(nums(a), true) },
  "STDEV.S": { min: 1, max: 255, sig: "STDEV.S(value1, [value2, …])", about: "How spread out numbers are (a sample).", run: (a) => spread(nums(a), true) },
  STDEVP: { min: 1, max: 255, sig: "STDEVP(value1, [value2, …])", about: "How spread out numbers are (all of them).", run: (a) => spread(nums(a), false) },
  "STDEV.P": { min: 1, max: 255, sig: "STDEV.P(value1, [value2, …])", about: "How spread out numbers are (all of them).", run: (a) => spread(nums(a), false) },
  PERCENTILE: { min: 2, max: 2, sig: "PERCENTILE(data, percent)", about: "The number a percent of the data sits under: PERCENTILE(B2:B9, 0.9).", run: (a) => percentile(a) },
  "PERCENTILE.INC": { min: 2, max: 2, sig: "PERCENTILE.INC(data, percent)", about: "The number a percent of the data sits under.", run: (a) => percentile(a) },
  RANK: { min: 2, max: 3, sig: "RANK(value, data, [ascending])", about: "Where a number places in a list, 1 for the largest.", run: (a) => rank(a) },
  "RANK.EQ": { min: 2, max: 3, sig: "RANK.EQ(value, data, [ascending])", about: "Where a number places in a list, 1 for the largest.", run: (a) => rank(a) },
  LARGE: { min: 2, max: 2, sig: "LARGE(data, n)", about: "The nth largest number.", run: (a) => nth(a, true) },
  SMALL: { min: 2, max: 2, sig: "SMALL(data, n)", about: "The nth smallest number.", run: (a) => nth(a, false) },
  // ------------------------------------------------------------- conditions
  IF: {
    min: 2,
    max: 3,
    sig: "IF(condition, value_if_true, [value_if_false])",
    about: "One value when true, another when false.",
    run: (a) => (a.bool(0) ? (a.has(1) ? a.val(1) : 0) : a.n > 2 ? (a.has(2) ? a.val(2) : 0) : false),
  },
  IFS: {
    min: 2,
    max: 254,
    sig: "IFS(condition1, value1, [condition2, value2, …])",
    about: "The value of the first condition that is true.",
    run: (a) => {
      if (a.n % 2) fail("#N/A", "IFS takes conditions and values in pairs.");
      for (let i = 0; i < a.n; i += 2) if (a.bool(i)) return a.val(i + 1);
      return fail("#N/A", "No condition was true.");
    },
  },
  IFERROR: {
    min: 1,
    max: 2,
    sig: "IFERROR(value, [value_if_error])",
    about: "A value, or another when it is an error.",
    run: (a) => {
      const v = a.val(0);
      return isErr(scalar(v)) && !isBlock(v) ? (a.has(1) ? a.val(1) : "") : v;
    },
  },
  IFNA: {
    min: 2,
    max: 2,
    sig: "IFNA(value, value_if_na)",
    about: "A value, or another when it is #N/A.",
    run: (a) => {
      const v = a.val(0);
      const s = scalar(v);
      return isErr(s) && s.code === "#N/A" && !isBlock(v) ? a.val(1) : v;
    },
  },
  AND: { min: 1, max: 255, sig: "AND(condition1, [condition2, …])", about: "TRUE when every condition is.", run: (a) => bools(a).every(Boolean) },
  OR: { min: 1, max: 255, sig: "OR(condition1, [condition2, …])", about: "TRUE when any condition is.", run: (a) => bools(a).some(Boolean) },
  NOT: { min: 1, max: 1, sig: "NOT(condition)", about: "The opposite of TRUE or FALSE.", run: (a) => !a.bool(0) },
  SWITCH: {
    min: 3,
    max: 255,
    sig: "SWITCH(value, case1, result1, [… default])",
    about: "The result for the case that matches.",
    run: (a) => {
      const v = a.sc(0);
      if (isErr(v)) throw v;
      let i = 1;
      for (; i + 1 < a.n; i += 2) if (same(a.sc(i), v)) return a.val(i + 1);
      return i < a.n ? a.val(i) : fail("#N/A", "No case matched.");
    },
  },
  SUMIF: {
    min: 2,
    max: 3,
    sig: "SUMIF(range, criterion, [sum_range])",
    about: "Adds the cells that meet a condition.",
    run: (a) => {
      const range = a.val(0);
      const d = dims(range);
      const test = criterion(a.sc(1), a.dmy);
      const target = a.has(2) ? a.val(2) : range;
      let s = 0;
      for (const [i, j] of matching(d, [{ v: range, test }], a.budget)) {
        const v = atIn(target, i, j);
        if (isErr(v)) throw v;
        if (typeof v === "number") s += v;
      }
      return s;
    },
  },
  SUMIFS: {
    min: 3,
    max: 255,
    sig: "SUMIFS(sum_range, range1, criterion1, [range2, criterion2, …])",
    about: "Adds the cells that meet every condition.",
    run: (a) => {
      const target = a.val(0);
      const d = dims(target);
      let s = 0;
      for (const [i, j] of matching(d, critPairs(a, 1, d), a.budget)) {
        const v = atIn(target, i, j);
        if (isErr(v)) throw v;
        if (typeof v === "number") s += v;
      }
      return s;
    },
  },
  COUNTIF: {
    min: 2,
    max: 2,
    sig: "COUNTIF(range, criterion)",
    about: "How many cells meet a condition.",
    run: (a) => {
      const range = a.val(0);
      let n = 0;
      for (const _ of matching(dims(range), [{ v: range, test: criterion(a.sc(1), a.dmy) }], a.budget)) n++;
      return n;
    },
  },
  COUNTIFS: {
    min: 2,
    max: 254,
    sig: "COUNTIFS(range1, criterion1, [range2, criterion2, …])",
    about: "How many cells meet every condition.",
    run: (a) => {
      const d = dims(a.val(0));
      let n = 0;
      for (const _ of matching(d, critPairs(a, 0, d), a.budget)) n++;
      return n;
    },
  },
  AVERAGEIF: {
    min: 2,
    max: 3,
    sig: "AVERAGEIF(range, criterion, [average_range])",
    about: "The average of the cells that meet a condition.",
    run: (a) => {
      const range = a.val(0);
      const target = a.has(2) ? a.val(2) : range;
      const xs: number[] = [];
      for (const [i, j] of matching(dims(range), [{ v: range, test: criterion(a.sc(1), a.dmy) }], a.budget)) {
        const v = atIn(target, i, j);
        if (isErr(v)) throw v;
        if (typeof v === "number") xs.push(v);
      }
      return xs.length ? sum(xs) / xs.length : fail("#DIV/0!", "No cells met the condition.");
    },
  },
  AVERAGEIFS: {
    min: 3,
    max: 255,
    sig: "AVERAGEIFS(average_range, range1, criterion1, …)",
    about: "The average of the cells that meet every condition.",
    run: (a) => {
      const target = a.val(0);
      const d = dims(target);
      const xs: number[] = [];
      for (const [i, j] of matching(d, critPairs(a, 1, d), a.budget)) {
        const v = atIn(target, i, j);
        if (typeof v === "number") xs.push(v);
      }
      return xs.length ? sum(xs) / xs.length : fail("#DIV/0!", "No cells met the conditions.");
    },
  },
  MINIFS: { min: 3, max: 255, sig: "MINIFS(range, range1, criterion1, …)", about: "The smallest number that meets every condition.", run: (a) => ((xs) => (xs.length ? Math.min(...xs) : 0))(ifsNums(a)) },
  MAXIFS: { min: 3, max: 255, sig: "MAXIFS(range, range1, criterion1, …)", about: "The largest number that meets every condition.", run: (a) => ((xs) => (xs.length ? Math.max(...xs) : 0))(ifsNums(a)) },
  // ------------------------------------------------------------- lookups
  VLOOKUP: {
    min: 3,
    max: 4,
    sig: "VLOOKUP(key, range, column, [is_sorted])",
    about: "Finds a key in the first column, gives a value from its row.",
    run: (a) => {
      const key = a.sc(0);
      if (isErr(key)) throw key;
      const range = a.val(1);
      const k = Math.trunc(a.num(2));
      const d = dims(range);
      if (k < 1 || k > d.w) fail(k < 1 ? "#VALUE!" : "#REF!", `Column ${k} is outside the range.`);
      a.budget.spend(d.h);
      const keys = col(range, 0);
      const sorted = a.has(3) ? a.bool(3) : true;
      const i = sorted ? sortedIndex(keys, key) : lookupIndex(keys, key, "exact", true);
      return i < 0 ? fail("#N/A", `${showKey(key)} was not found.`) : atIn(range, i, k - 1);
    },
  },
  HLOOKUP: {
    min: 3,
    max: 4,
    sig: "HLOOKUP(key, range, row, [is_sorted])",
    about: "Finds a key in the first row, gives a value from its column.",
    run: (a) => {
      const key = a.sc(0);
      if (isErr(key)) throw key;
      const range = a.val(1);
      const k = Math.trunc(a.num(2));
      const d = dims(range);
      if (k < 1 || k > d.h) fail(k < 1 ? "#VALUE!" : "#REF!", `Row ${k} is outside the range.`);
      a.budget.spend(d.w);
      const keys = row(range, 0);
      const sorted = a.has(3) ? a.bool(3) : true;
      const j = sorted ? sortedIndex(keys, key) : lookupIndex(keys, key, "exact", true);
      return j < 0 ? fail("#N/A", `${showKey(key)} was not found.`) : atIn(range, k - 1, j);
    },
  },
  XLOOKUP: {
    min: 3,
    max: 6,
    sig: "XLOOKUP(key, lookup_range, result_range, [if_not_found], [match_mode], [search_mode])",
    about: "Finds a key and gives the matching value from another range.",
    run: (a) => {
      const key = a.sc(0);
      if (isErr(key)) throw key;
      const look = a.val(1);
      const res = a.val(2);
      const keys = line(look);
      a.budget.spend(keys.length);
      const mode = a.has(4) ? Math.trunc(a.num(4)) : 0;
      const last = a.has(5) && a.num(5) < 0;
      const i = lookupIndex(keys, key, mode === -1 ? "below" : mode === 1 ? "above" : "exact", mode === 2, last);
      const exact = i >= 0 && same(keys[i], key, mode === 2);
      const idx = mode === 0 || mode === 2 ? (exact ? i : -1) : i;
      if (idx < 0) return a.has(3) ? a.val(3) : fail("#N/A", `${showKey(key)} was not found.`);
      const ld = dims(look);
      const rd = dims(res);
      // a column looked up gives the result's row; a row gives its column
      if (ld.w === 1) return rd.w === 1 ? atIn(res, idx, 0) : new Arr([row(res, idx)]);
      return rd.h === 1 ? atIn(res, 0, idx) : new Arr(col(res, idx).map((v) => [v]));
    },
  },
  INDEX: {
    min: 1,
    max: 3,
    sig: "INDEX(range, row, [column])",
    about: "The value at a row and column of a range.",
    run: (a) => {
      const range = a.val(0);
      const d = dims(range);
      let r = a.has(1) ? Math.trunc(a.num(1)) : 0;
      let c = a.has(2) ? Math.trunc(a.num(2)) : 0;
      // one row or column: a single number picks along it
      if (a.n < 3 && d.h === 1 && d.w > 1) {
        c = r;
        r = 1;
      }
      if (r < 0 || c < 0 || r > d.h || c > (d.w || 1)) fail("#REF!", "That row or column is outside the range.");
      if (r === 0 && c === 0) return range;
      if (r === 0) return new Arr(col(range, c - 1).map((v) => [v]));
      if (c === 0) return d.w === 1 ? atIn(range, r - 1, 0) : new Arr([row(range, r - 1)]);
      return atIn(range, r - 1, c - 1);
    },
  },
  MATCH: {
    min: 2,
    max: 3,
    sig: "MATCH(key, range, [type])",
    about: "Where a key sits in a row or column, from 1.",
    run: (a) => {
      const key = a.sc(0);
      if (isErr(key)) throw key;
      const keys = line(a.val(1));
      a.budget.spend(keys.length);
      const t = a.has(2) ? Math.trunc(a.num(2)) : 1;
      const i = t === 0 ? lookupIndex(keys, key, "exact", true) : sortedIndex(keys, key, t < 0);
      return i < 0 ? fail("#N/A", `${showKey(key)} was not found.`) : i + 1;
    },
  },
  ROWS: { min: 1, max: 1, sig: "ROWS(range)", about: "How many rows a range has.", run: (a) => dims(a.val(0)).h },
  COLUMNS: { min: 1, max: 1, sig: "COLUMNS(range)", about: "How many columns a range has.", run: (a) => dims(a.val(0)).w },
  ROW: { min: 0, max: 1, sig: "ROW([cell])", about: "A cell's row number.", run: (a) => ((v) => (v instanceof RangeV ? v.r0 + 1 : a.here.r + 1))(a.n ? a.val(0) : null) },
  COLUMN: { min: 0, max: 1, sig: "COLUMN([cell])", about: "A cell's column number.", run: (a) => ((v) => (v instanceof RangeV ? v.c0 + 1 : a.here.c + 1))(a.n ? a.val(0) : null) },
  UNIQUE: {
    min: 1,
    max: 1,
    sig: "UNIQUE(range)",
    about: "The rows of a range without repeats.",
    run: (a) => {
      const rows = grid(a.val(0), a.budget);
      const seen = new Set<string>();
      const out: Scalar[][] = [];
      for (const r of rows) {
        if (r.every((v) => v === null)) continue;
        const k = JSON.stringify(r.map((v) => (typeof v === "string" ? v.toLowerCase() : isErr(v) ? v.code : typeof v === "number" ? sig15(v) : v)));
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(r);
      }
      return out.length ? new Arr(out) : fail("#N/A", "There is nothing to list.");
    },
  },
  SORT: {
    min: 1,
    max: 255,
    sig: "SORT(range, [column], [ascending], …)",
    about: "The rows of a range in order.",
    run: (a) => {
      const rows = grid(a.val(0), a.budget).filter((r) => r.some((v) => v !== null));
      const keys: { c: number; asc: boolean }[] = [];
      for (let i = 1; i < a.n; i += 2) keys.push({ c: Math.trunc(a.num(i, 1)) - 1, asc: a.has(i + 1) ? a.bool(i + 1) : true });
      if (!keys.length) keys.push({ c: 0, asc: true });
      if (keys.some((k) => k.c < 0 || k.c >= (rows[0]?.length ?? 1))) fail("#VALUE!", "That column is outside the range.");
      const sorted = [...rows].sort((x, y) => {
        for (const k of keys) {
          const d = sortCmp(x[k.c], y[k.c]);
          if (d) return k.asc ? d : -d;
        }
        return 0;
      });
      return sorted.length ? new Arr(sorted) : fail("#N/A", "There is nothing to sort.");
    },
  },
  FILTER: {
    min: 2,
    max: 30,
    sig: "FILTER(range, condition1, [condition2, …])",
    about: "The rows (or columns) of a range where every condition is TRUE.",
    run: (a) => {
      const src = a.val(0);
      const d = dims(src);
      a.budget.spend(d.h * d.w * a.n);
      const conds = Array.from({ length: a.n - 1 }, (_, i) => a.val(i + 1));
      const byRow = conds.every((x) => dims(x).h === d.h && dims(x).w === 1);
      const byCol = !byRow && conds.every((x) => dims(x).w === d.w && dims(x).h === 1);
      if (!byRow && !byCol) fail("#VALUE!", "Each condition is one column as tall as the range, or one row as wide as it.");
      const keep = (k: number) =>
        conds.every((x) => {
          const v = byRow ? atIn(x, k, 0) : atIn(x, 0, k);
          return typeof v === "boolean" ? v : typeof v === "number" ? v !== 0 : false;
        });
      const rows = grid(src, a.budget);
      if (byRow) {
        const picked: number[] = [];
        for (let i = 0; i < d.h; i++) if (keep(i)) picked.push(i);
        if (!picked.length) fail("#N/A", "No rows meet the conditions.");
        return new Arr(
          picked.map((i) => rows[i]),
          src instanceof RangeV ? { range: src, rows: picked } : undefined,
        );
      }
      const cols: number[] = [];
      for (let j = 0; j < d.w; j++) if (keep(j)) cols.push(j);
      if (!cols.length) fail("#N/A", "No columns meet the conditions.");
      return new Arr(rows.map((row) => cols.map((j) => row[j])));
    },
  },
  SORTBY: {
    min: 2,
    max: 255,
    sig: "SORTBY(range, by_range1, [order1], [by_range2, order2, …])",
    about: "The rows of a range in the order of other columns (1 A to Z, -1 Z to A).",
    run: (a) => {
      const src = a.val(0);
      const d = dims(src);
      const rows = grid(src, a.budget);
      const keys: { v: Val; dir: number }[] = [];
      for (let i = 1; i < a.n; i += 2) {
        const v = a.val(i);
        if (dims(v).h !== d.h || dims(v).w !== 1) fail("#VALUE!", "Each range to sort by is one column as tall as the range.");
        const dir = a.has(i + 1) ? Math.trunc(a.num(i + 1)) : 1;
        if (dir !== 1 && dir !== -1) fail("#VALUE!", "The order is 1 (A to Z) or -1 (Z to A).");
        keys.push({ v, dir });
      }
      const order = rows.map((_, i) => i);
      order.sort((x, y) => {
        for (const k of keys) {
          const c = sortCmp(atIn(k.v, x, 0), atIn(k.v, y, 0));
          if (c) return c * k.dir;
        }
        return x - y;
      });
      return new Arr(
        order.map((i) => rows[i]),
        src instanceof RangeV ? { range: src, rows: order } : undefined,
      );
    },
  },
  TRANSPOSE: {
    min: 1,
    max: 1,
    sig: "TRANSPOSE(range)",
    about: "A range with its rows as columns.",
    run: (a) => {
      const rows = grid(a.val(0), a.budget);
      const h = rows.length;
      const w = rows[0]?.length ?? 0;
      return new Arr(Array.from({ length: w }, (_, j) => Array.from({ length: h }, (_, i) => rows[i][j])));
    },
  },
  SEQUENCE: {
    min: 1,
    max: 4,
    sig: "SEQUENCE(rows, [columns], [start], [step])",
    about: "A block of numbers in order: SEQUENCE(5) is 1 to 5 down.",
    run: (a) => {
      const h = Math.trunc(a.num(0));
      const w = a.has(1) ? Math.trunc(a.num(1)) : 1;
      const start = a.has(2) ? a.num(2) : 1;
      const step = a.has(3) ? a.num(3) : 1;
      if (h < 1 || w < 1) fail("#VALUE!", "SEQUENCE takes 1 or more rows and columns.");
      if (h * w > 200_000) fail("#VALUE!", "The result is too large to show.");
      return new Arr(Array.from({ length: h }, (_, i) => Array.from({ length: w }, (_, j) => start + (i * w + j) * step)));
    },
  },
  CHOOSE: {
    min: 2,
    max: 255,
    sig: "CHOOSE(index, choice1, [choice2, …])",
    about: "The choice at a place: CHOOSE(2, \"a\", \"b\") is b.",
    run: (a) => {
      const i = Math.trunc(a.num(0));
      return i >= 1 && i < a.n ? a.val(i) : fail("#VALUE!", `CHOOSE has choices 1 to ${a.n - 1}.`);
    },
  },
  XMATCH: {
    min: 2,
    max: 4,
    sig: "XMATCH(key, range, [match_mode], [search_mode])",
    about: "Where a key sits in a row or column, from 1, or the nearest.",
    run: (a) => {
      const key = a.sc(0);
      if (isErr(key)) throw key;
      const keys = line(a.val(1));
      a.budget.spend(keys.length);
      const mode = a.has(2) ? Math.trunc(a.num(2)) : 0;
      if (![0, -1, 1, 2].includes(mode)) fail("#VALUE!", "match_mode is 0, -1, 1 or 2.");
      const search = a.has(3) ? Math.trunc(a.num(3)) : 1;
      if (![1, -1, 2, -2].includes(search)) fail("#VALUE!", "search_mode is 1, -1, 2 or -2.");
      const i = lookupIndex(keys, key, mode === -1 ? "below" : mode === 1 ? "above" : "exact", mode === 2, search < 0);
      const exact = i >= 0 && same(keys[i], key, mode === 2);
      const idx = mode === 0 || mode === 2 ? (exact ? i : -1) : i;
      return idx < 0 ? fail("#N/A", `${showKey(key)} was not found.`) : idx + 1;
    },
  },
  OFFSET: {
    min: 3,
    max: 5,
    sig: "OFFSET(reference, rows, columns, [height], [width])",
    about: "The cells some rows and columns away from a reference.",
    run: (a) => {
      const ref = a.val(0);
      if (!(ref instanceof RangeV)) return fail("#REF!", "OFFSET starts from a cell or a range, such as A1.");
      const r0 = ref.r0 + Math.trunc(a.num(1));
      const c0 = ref.c0 + Math.trunc(a.num(2));
      const h = a.has(3) ? Math.trunc(a.num(3)) : ref.r1 - ref.r0 + 1;
      const w = a.has(4) ? Math.trunc(a.num(4)) : ref.c1 - ref.c0 + 1;
      if (h < 1 || w < 1) fail("#REF!", "The height and width are 1 or more.");
      if (r0 < 0 || c0 < 0 || r0 + h > MAX_ROWS || c0 + w > MAX_COLS) fail("#REF!", "That is outside the sheet.");
      return new RangeV(ref.tab, r0, c0, r0 + h - 1, c0 + w - 1);
    },
  },
  INDIRECT: {
    min: 1,
    max: 2,
    sig: 'INDIRECT("reference", [is_A1])',
    about: 'The cells a reference written as text names, such as INDIRECT("B"&A1).',
    run: (a) => {
      if (a.has(1) && !a.bool(1)) fail("#REF!", "INDIRECT reads A1-style references here, such as \"B2\" or \"'Tab name'!A1:C9\".");
      if (!a.ref) return fail("#REF!", "INDIRECT cannot be worked out here.");
      return a.ref(a.str(0));
    },
  },
  ARRAYFORMULA: {
    min: 1,
    max: 1,
    sig: "ARRAYFORMULA(formula)",
    about: "A formula over whole ranges; here every formula already works so.",
    run: (a) => a.val(0),
  },
  // ------------------------------------------------------------- text
  CONCAT: { min: 1, max: 255, sig: "CONCAT(value1, value2, …)", about: "Joins text.", run: (a) => joinAll(a, 0, "", false) },
  CONCATENATE: { min: 1, max: 255, sig: "CONCATENATE(value1, [value2, …])", about: "Joins text.", run: (a) => joinAll(a, 0, "", false) },
  TEXTJOIN: { min: 3, max: 255, sig: "TEXTJOIN(delimiter, ignore_empty, text1, …)", about: "Joins text with a separator between.", run: (a) => joinAll(a, 2, a.str(0), a.bool(1)) },
  LEFT: { min: 1, max: 2, sig: "LEFT(text, [count])", about: "The first characters of text.", run: (a) => [...text(a, 0)].slice(0, count(a, 1)).join("") },
  RIGHT: {
    min: 1,
    max: 2,
    sig: "RIGHT(text, [count])",
    about: "The last characters of text.",
    run: (a) => {
      const s = [...text(a, 0)];
      const n = count(a, 1);
      return n ? s.slice(-n).join("") : "";
    },
  },
  MID: {
    min: 3,
    max: 3,
    sig: "MID(text, start, count)",
    about: "Characters from the middle of text.",
    run: (a) => {
      const start = Math.trunc(a.num(1));
      const n = Math.trunc(a.num(2));
      if (start < 1 || n < 0) fail("#VALUE!", "MID starts at 1 and takes 0 or more characters.");
      return [...text(a, 0)].slice(start - 1, start - 1 + n).join("");
    },
  },
  LEN: { min: 1, max: 1, sig: "LEN(text)", about: "How many characters.", run: (a) => [...text(a, 0)].length },
  UPPER: { min: 1, max: 1, sig: "UPPER(text)", about: "Text in capitals.", run: (a) => text(a, 0).toUpperCase() },
  LOWER: { min: 1, max: 1, sig: "LOWER(text)", about: "Text in small letters.", run: (a) => text(a, 0).toLowerCase() },
  PROPER: { min: 1, max: 1, sig: "PROPER(text)", about: "Each word with a capital.", run: (a) => text(a, 0).toLowerCase().replace(/(^|[^\p{L}\p{N}'])(\p{L})/gu, (_, p: string, c: string) => p + c.toUpperCase()) },
  TRIM: { min: 1, max: 1, sig: "TRIM(text)", about: "Text without extra spaces.", run: (a) => text(a, 0).replace(/ +/g, " ").trim() },
  SUBSTITUTE: {
    min: 3,
    max: 4,
    sig: "SUBSTITUTE(text, find, replace, [which])",
    about: "Replaces words in text.",
    run: (a) => {
      const s = text(a, 0);
      const f = text(a, 1);
      const r = text(a, 2);
      if (!f) return s;
      if (!a.has(3)) return s.split(f).join(r);
      const n = Math.trunc(a.num(3));
      if (n < 1) fail("#VALUE!", "Which one is 1 or more.");
      let at = -1;
      for (let k = 0; k < n; k++) {
        at = s.indexOf(f, at + 1);
        if (at < 0) return s;
      }
      return s.slice(0, at) + r + s.slice(at + f.length);
    },
  },
  FIND: { min: 2, max: 3, sig: "FIND(find, text, [start])", about: "Where text starts in other text (case counts).", run: (a) => findIn(a, false) },
  SEARCH: { min: 2, max: 3, sig: "SEARCH(find, text, [start])", about: "Where text starts in other text (any case, * and ?).", run: (a) => findIn(a, true) },
  REPT: { min: 2, max: 2, sig: "REPT(text, times)", about: "Text repeated.", run: (a) => ((s, n) => (n < 0 || s.length * n > 50_000 ? fail("#VALUE!", "That would be too long.") : s.repeat(n)))(text(a, 0), Math.trunc(a.num(1))) },
  CHAR: {
    min: 1,
    max: 1,
    sig: "CHAR(number)",
    about: "The character with that number, such as CHAR(10) for a new line.",
    run: (a) => {
      const n = Math.trunc(a.num(0));
      return n < 1 || n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff) ? fail("#VALUE!", "CHAR takes a number from 1 to 1114111.") : String.fromCodePoint(n);
    },
  },
  EXACT: { min: 2, max: 2, sig: "EXACT(text1, text2)", about: "TRUE when two texts are the same, case and all.", run: (a) => text(a, 0) === text(a, 1) },
  TEXT: {
    min: 2,
    max: 2,
    sig: 'TEXT(value, "format")',
    about: 'A number as text in a format, such as "0.00" or "mmm d".',
    run: (a) => {
      const v = a.sc(0);
      if (isErr(v)) throw v;
      const n = typeof v === "number" ? v : typeof v === "boolean" ? null : textNumber(String(v ?? ""), a.dmy);
      if (n === null) return toStr(v) as string;
      return formatCode(n, a.str(1));
    },
  },
  VALUE: {
    min: 1,
    max: 1,
    sig: "VALUE(text)",
    about: "Text read as a number.",
    run: (a) => {
      const v = a.sc(0);
      if (typeof v === "number") return v;
      if (isErr(v)) throw v;
      const n = textNumber(String(v ?? "") || "0", a.dmy);
      return n === null ? fail("#VALUE!", `"${v}" is not a number.`) : n;
    },
  },
  SPLIT: {
    min: 2,
    max: 4,
    sig: 'SPLIT(text, delimiter, [split_by_each], [remove_empty])',
    about: 'Text split into cells across: SPLIT("a,b", ",").',
    run: (a) => {
      const s = text(a, 0);
      const by = text(a, 1);
      if (!by) fail("#VALUE!", "SPLIT needs something to split by.");
      const each = a.has(2) ? a.bool(2) : true;
      const dropEmpty = a.has(3) ? a.bool(3) : true;
      let parts = each ? s.split(new RegExp(`[${by.replace(/[\\\]^-]/g, "\\$&")}]`)) : s.split(by);
      if (dropEmpty) parts = parts.filter((p) => p !== "");
      if (!parts.length) fail("#VALUE!", "There is nothing to split.");
      if (parts.length > 2000) fail("#VALUE!", "That would be too many cells.");
      // each part read as a person typing it would be: 12 a number, 03/04/2026 a date
      return new Arr([parts.map((p) => readInput(p, a.dmy).v)]);
    },
  },
  REGEXMATCH: { min: 2, max: 2, sig: "REGEXMATCH(text, pattern)", about: "TRUE when the text matches a pattern.", run: (a) => ((t) => safeRegex(a.str(1), "", t.length).test(t))(regexText(a)) },
  REGEXEXTRACT: {
    min: 2,
    max: 2,
    sig: "REGEXEXTRACT(text, pattern)",
    about: "The part of the text a pattern matches (its groups, when it has some).",
    run: (a) => {
      const t = regexText(a);
      const m = safeRegex(a.str(1), "", t.length).exec(t);
      if (!m) return fail("#N/A", "The pattern matched nothing.");
      if (m.length <= 1) return m[0];
      return m.length === 2 ? (m[1] ?? "") : new Arr([m.slice(1).map((x) => x ?? "")]);
    },
  },
  REGEXREPLACE: {
    min: 3,
    max: 3,
    sig: "REGEXREPLACE(text, pattern, replacement)",
    about: "Text with every match of a pattern replaced ($1 for a group).",
    run: (a) => {
      const t = regexText(a);
      const out = t.replace(safeRegex(a.str(1), "g", t.length), a.str(2));
      return out.length > 50_000 ? fail("#VALUE!", "The text would be too long.") : out;
    },
  },
  ENCODEURL: { min: 1, max: 1, sig: "ENCODEURL(text)", about: "Text made safe to put in a web address.", run: (a) => encodeURIComponent(text(a, 0)) },
  ISURL: { min: 1, max: 1, sig: "ISURL(value)", about: "TRUE when the value is a web address.", run: (a) => ((v) => typeof v === "string" && isUrl(v))(a.sc(0)) },
  // ------------------------------------------------------------- dates
  TODAY: { min: 0, max: 0, sig: "TODAY()", about: "Today's date.", run: (a) => Math.floor(a.now) },
  NOW: { min: 0, max: 0, sig: "NOW()", about: "The date and time now.", run: (a) => a.now },
  DATE: {
    min: 3,
    max: 3,
    sig: "DATE(year, month, day)",
    about: "A date from its year, month and day.",
    run: (a) => {
      let y = Math.trunc(a.num(0));
      if (y >= 0 && y < 1900) y += 1900;
      if (y < 0 || y > 9999) fail("#NUM!", "The year is out of range.");
      return serialOf(y, Math.trunc(a.num(1)), Math.trunc(a.num(2)));
    },
  },
  TIME: { min: 3, max: 3, sig: "TIME(hour, minute, second)", about: "A time of day.", run: (a) => ((t) => (t < 0 ? fail("#NUM!", "A time is not negative.") : (t % 86400) / 86400))(Math.trunc(a.num(0)) * 3600 + Math.trunc(a.num(1)) * 60 + Math.trunc(a.num(2))) },
  YEAR: { min: 1, max: 1, sig: "YEAR(date)", about: "A date's year.", run: (a) => ymd(dateArg(a, 0)).y },
  MONTH: { min: 1, max: 1, sig: "MONTH(date)", about: "A date's month, 1 to 12.", run: (a) => ymd(dateArg(a, 0)).m },
  DAY: { min: 1, max: 1, sig: "DAY(date)", about: "A date's day of the month.", run: (a) => ymd(dateArg(a, 0)).d },
  HOUR: { min: 1, max: 1, sig: "HOUR(time)", about: "A time's hour.", run: (a) => Math.floor(((a.num(0) % 1) + 1e-9) * 24) % 24 },
  MINUTE: { min: 1, max: 1, sig: "MINUTE(time)", about: "A time's minute.", run: (a) => Math.floor(((a.num(0) % 1) * 1440 + 1e-7) % 60) },
  WEEKDAY: {
    min: 1,
    max: 2,
    sig: "WEEKDAY(date, [type])",
    about: "The day of the week as a number (1 is Sunday).",
    run: (a) => {
      const w = weekdayOf(dateArg(a, 0));
      const t = a.has(1) ? Math.trunc(a.num(1)) : 1;
      if (t === 1) return w + 1;
      if (t === 2) return ((w + 6) % 7) + 1;
      if (t === 3) return (w + 6) % 7;
      return fail("#NUM!", "WEEKDAY's type is 1, 2 or 3.");
    },
  },
  EOMONTH: { min: 2, max: 2, sig: "EOMONTH(start, months)", about: "The last day of a month before or after a date.", run: (a) => addMonths(dateArg(a, 0), a.num(1), true) },
  EDATE: { min: 2, max: 2, sig: "EDATE(start, months)", about: "A date some months before or after.", run: (a) => addMonths(dateArg(a, 0), a.num(1), false) },
  DATEDIF: {
    min: 3,
    max: 3,
    sig: 'DATEDIF(start, end, "unit")',
    about: 'Time between dates in "Y", "M", "D", "MD", "YM" or "YD".',
    run: (a) => {
      const s = dateArg(a, 0);
      const e = dateArg(a, 1);
      if (s > e) fail("#NUM!", "The start date is after the end date.");
      const u = a.str(2).toUpperCase();
      const A = ymd(s);
      const B = ymd(e);
      let months = (B.y - A.y) * 12 + (B.m - A.m);
      if (B.d < A.d) months--;
      switch (u) {
        case "D":
          return e - s;
        case "M":
          return months;
        case "Y":
          return Math.floor(months / 12);
        case "YM":
          return months % 12;
        case "MD": {
          if (B.d >= A.d) return B.d - A.d;
          const prev = new Date(Date.UTC(B.y, B.m - 1, 0)).getUTCDate();
          return prev - A.d + B.d;
        }
        case "YD": {
          let start = serialOf(B.y, A.m, A.d);
          if (start > e) start = serialOf(B.y - 1, A.m, A.d);
          return e - start;
        }
        default:
          return fail("#NUM!", 'The unit is "Y", "M", "D", "MD", "YM" or "YD".');
      }
    },
  },
  NETWORKDAYS: {
    min: 2,
    max: 3,
    sig: "NETWORKDAYS(start, end, [holidays])",
    about: "Working days between two dates, both counted.",
    run: (a) => {
      let s = dateArg(a, 0);
      let e = dateArg(a, 1);
      const sign = s > e ? -1 : 1;
      if (s > e) [s, e] = [e, s];
      if (e - s > 200_000) fail("#NUM!", "The dates are too far apart.");
      const off = new Set<number>();
      if (a.has(2)) for (const [v] of each(a.val(2), a.budget)) if (typeof v === "number") off.add(Math.floor(v));
      let n = 0;
      for (let d = s; d <= e; d++) {
        const w = weekdayOf(d);
        if (w !== 0 && w !== 6 && !off.has(d)) n++;
      }
      return sign * n;
    },
  },
  WORKDAY: {
    min: 2,
    max: 3,
    sig: "WORKDAY(start, days, [holidays])",
    about: "The working day some working days after (or before) a date.",
    run: (a) => {
      let d = dateArg(a, 0);
      let n = Math.trunc(a.num(1));
      if (Math.abs(n) > 100_000) fail("#NUM!", "That is too many days.");
      const off = new Set<number>();
      if (a.has(2)) for (const [v] of each(a.val(2), a.budget)) if (typeof v === "number") off.add(Math.floor(v));
      const step = n < 0 ? -1 : 1;
      while (n !== 0) {
        d += step;
        const w = weekdayOf(d);
        if (w !== 0 && w !== 6 && !off.has(d)) n -= step;
      }
      return d;
    },
  },
  DAYS: { min: 2, max: 2, sig: "DAYS(end, start)", about: "The days from one date to another.", run: (a) => dateArg(a, 0) - dateArg(a, 1) },
  ISOWEEKNUM: {
    min: 1,
    max: 1,
    sig: "ISOWEEKNUM(date)",
    about: "A date's week of the year, as ISO counts it (weeks start on Monday).",
    run: (a) => {
      const d = dateArg(a, 0);
      // the Thursday of its week sits in the year the week belongs to
      const thu = d - ((weekdayOf(d) + 6) % 7) + 3;
      const jan1 = serialOf(ymd(thu).y, 1, 1);
      return Math.floor((thu - jan1) / 7) + 1;
    },
  },
  DATEVALUE: {
    min: 1,
    max: 1,
    sig: 'DATEVALUE("date")',
    about: "Text read as a date, the sheet's way (day first on a UK sheet).",
    run: (a) => {
      const v = a.sc(0);
      if (isErr(v)) throw v;
      if (typeof v === "number") return Math.floor(v);
      const r = readInput(String(v ?? "").trim(), a.dmy);
      return typeof r.v === "number" && (r.implied?.nf === "date" || r.implied?.nf === "datetime") ? Math.floor(r.v) : fail("#VALUE!", `"${String(v ?? "").slice(0, 40)}" is not a date.`);
    },
  },
  // ------------------------------------------------------------- money
  PMT: {
    min: 3,
    max: 5,
    sig: "PMT(rate, periods, present_value, [future_value], [end_or_start])",
    about: "A loan's payment each period.",
    run: (a) => {
      const r = a.num(0);
      const n = a.num(1);
      const pv = a.num(2);
      const fv = a.num(3, 0);
      const t = a.num(4, 0) ? 1 : 0;
      if (n === 0) fail("#NUM!", "The number of periods is not zero.");
      if (r === 0) return -(pv + fv) / n;
      const f = Math.pow(1 + r, n);
      return ok(-(r * (fv + pv * f)) / ((1 + r * t) * (f - 1)));
    },
  },
  // ------------------------------------------------------------- links
  HYPERLINK: {
    min: 1,
    max: 2,
    sig: "HYPERLINK(url, [label])",
    about: "A link: the label shows, a click opens the address.",
    run: (a) => {
      const url = a.str(0);
      const label = a.has(1) ? a.sc(1) : url;
      if (isErr(label)) throw label;
      // only a web or mail address is ever a link; anything else shows as its label
      const ln = linkOf(url);
      if (ln) a.setLink?.(ln);
      return label === null ? url : label;
    },
  },
  // ------------------------------------------------------------- information
  ISBLANK: { min: 1, max: 1, sig: "ISBLANK(value)", about: "TRUE for an empty cell.", run: (a) => a.sc(0) === null },
  ISNUMBER: { min: 1, max: 1, sig: "ISNUMBER(value)", about: "TRUE for a number.", run: (a) => typeof a.sc(0) === "number" },
  ISTEXT: { min: 1, max: 1, sig: "ISTEXT(value)", about: "TRUE for text.", run: (a) => typeof a.sc(0) === "string" },
  ISERROR: { min: 1, max: 1, sig: "ISERROR(value)", about: "TRUE for an error.", run: (a) => isErr(a.sc(0)) },
  ISLOGICAL: { min: 1, max: 1, sig: "ISLOGICAL(value)", about: "TRUE for TRUE or FALSE.", run: (a) => typeof a.sc(0) === "boolean" },
  ISNA: { min: 1, max: 1, sig: "ISNA(value)", about: "TRUE for #N/A.", run: (a) => ((v) => isErr(v) && v.code === "#N/A")(a.sc(0)) },
  NA: { min: 0, max: 0, sig: "NA()", about: "The #N/A error.", run: () => err("#N/A", "Not available.") },
};

function countWhere(a: Args, test: (v: Scalar, inBlock: boolean) => boolean): number {
  let n = 0;
  for (let i = 0; i < a.n; i++) {
    if (!a.has(i)) continue;
    for (const [v, inBlock] of each(a.val(i), a.budget)) if (test(v, inBlock)) n++;
  }
  return n;
}

function bools(a: Args): boolean[] {
  const out: boolean[] = [];
  for (let i = 0; i < a.n; i++) {
    if (!a.has(i)) continue;
    for (const [v, inBlock] of each(a.val(i), a.budget)) {
      if (isErr(v)) throw v;
      if (inBlock && (v === null || typeof v === "string")) continue;
      const b = toBool(v);
      if (isErr(b)) throw b;
      out.push(b);
    }
  }
  if (!out.length) fail("#VALUE!", "There are no TRUE or FALSE values.");
  return out;
}

function ifsNums(a: Args): number[] {
  const target = a.val(0);
  const d = dims(target);
  const xs: number[] = [];
  for (const [i, j] of matching(d, critPairs(a, 1, d), a.budget)) {
    const v = atIn(target, i, j);
    if (typeof v === "number") xs.push(v);
  }
  return xs;
}

function count(a: Args, i: number): number {
  const n = a.has(i) ? Math.trunc(a.num(i)) : 1;
  if (n < 0) fail("#VALUE!", "The count is 0 or more.");
  return n;
}

function joinAll(a: Args, from: number, sep: string, skipEmpty: boolean): string {
  const parts: string[] = [];
  for (let i = from; i < a.n; i++) {
    if (!a.has(i)) {
      if (!skipEmpty) parts.push("");
      continue;
    }
    for (const [v] of each(a.val(i), a.budget)) {
      if (isErr(v)) throw v;
      const s = toStr(v) as string;
      if (skipEmpty && s === "") continue;
      parts.push(s);
    }
  }
  const out = parts.join(sep);
  if (out.length > 50_000) fail("#VALUE!", "The text would be too long.");
  return out;
}

function findIn(a: Args, loose: boolean): number {
  const needle = text(a, 0);
  const hay = text(a, 1);
  const start = a.has(2) ? Math.trunc(a.num(2)) : 1;
  if (start < 1 || start > hay.length + 1) fail("#VALUE!", "The start is outside the text.");
  if (loose) {
    const re = new RegExp(wildcard(needle).source.slice(1, -1), "i");
    const m = re.exec(hay.slice(start - 1));
    return m ? m.index + start : fail("#VALUE!", `"${needle}" was not found.`);
  }
  const at = hay.indexOf(needle, start - 1);
  return at < 0 ? fail("#VALUE!", `"${needle}" was not found.`) : at + 1;
}

function showKey(k: Scalar): string {
  return typeof k === "string" ? `"${k}"` : typeof k === "number" ? numText(k) : String(k);
}

/** Standard deviation: of a sample (n - 1) or of all the numbers. */
function spread(xs: number[], sample: boolean): number {
  if (xs.length < (sample ? 2 : 1)) fail("#DIV/0!", sample ? "It takes 2 or more numbers." : "There are no numbers.");
  const m = sum(xs) / xs.length;
  let ss = 0;
  for (const x of xs) ss += (x - m) * (x - m);
  return Math.sqrt(ss / (xs.length - (sample ? 1 : 0)));
}

function percentile(a: Args): number {
  const xs = numsIn(a, 0).sort((x, y) => x - y);
  const p = a.num(1);
  if (!xs.length) fail("#NUM!", "There are no numbers.");
  if (p < 0 || p > 1) fail("#NUM!", "The percent is from 0 to 1, such as 0.9.");
  const at = p * (xs.length - 1);
  const lo = Math.floor(at);
  return lo + 1 < xs.length ? xs[lo] + (at - lo) * (xs[lo + 1] - xs[lo]) : xs[lo];
}

function rank(a: Args): number {
  const v = sig15(a.num(0));
  const xs = numsIn(a, 1).map(sig15);
  const up = a.has(2) && a.num(2) !== 0;
  if (!xs.includes(v)) return fail("#N/A", `${numText(v)} is not in the list.`);
  return xs.filter((x) => (up ? x < v : x > v)).length + 1;
}

function nth(a: Args, large: boolean): number {
  const xs = numsIn(a, 0).sort((x, y) => (large ? y - x : x - y));
  const n = Math.trunc(a.num(1));
  return n >= 1 && n <= xs.length ? xs[n - 1] : fail("#NUM!", `n is from 1 to ${xs.length}, how many numbers there are.`);
}

/** The numbers of one argument: a block's numbers (text skipped), or the value given. */
function numsIn(a: Args, i: number): number[] {
  const out: number[] = [];
  for (const [v, inBlock] of each(a.val(i), a.budget)) {
    if (isErr(v)) throw v;
    if (typeof v === "number") out.push(v);
    else if (!inBlock) out.push(ok(toNum(v, a.dmy)));
  }
  return out;
}

function regexText(a: Args): string {
  const s = text(a, 0);
  return s.length > 10_000 ? fail("#VALUE!", "The text is too long for a pattern (10,000 characters).") : s;
}

/**
 * A pattern as Google Sheets reads one (RE2), checked first so it cannot hold the board up: no back references or
 * look-arounds (RE2 has neither), no repeat of a part that repeats itself, such as (a+)+, and no run of repeats that
 * can match the same letters (a*a*a*b, .*a.*a.*b) on text so long that trying every way would be slow (slowCost). A
 * leading (?i) ignores case. n: the length of the text it runs on.
 */
function safeRegex(src: string, flags = "", n = 0): RegExp {
  const key = `${flags}\u0000${src}`;
  let kept = PATTERNS.get(key);
  if (!kept) {
    kept = checkRegex(src, flags);
    if (PATTERNS.size > 500) PATTERNS.clear();
    PATTERNS.set(key, kept);
  }
  if (slowCost(n, kept.run, kept.anchored) > 1e8) fail("#VALUE!", "That pattern repeats parts that match the same letters, which takes too long on text this long. Write it more simply, or on shorter text.");
  kept.re.lastIndex = 0;
  return kept.re;
}

/** Each pattern checked once: its expression, its longest run of repeats that overlap, and whether it starts with ^. */
const PATTERNS = new Map<string, { re: RegExp; run: number; anchored: boolean }>();

/** The worst a pattern can take on text of n letters: every place it may start (unless ^), times every way its run of overlapping repeats can split the text. */
function slowCost(n: number, run: number, anchored: boolean): number {
  if (!run) return n;
  const e = run + (anchored ? 0 : 1);
  let fact = 1;
  for (let i = 2; i <= e; i++) fact *= i;
  return Math.pow(Math.max(n, 1), e) / fact;
}

function checkRegex(src: string, flags: string): { re: RegExp; run: number; anchored: boolean } {
  let p = src;
  if (p.length > 500) fail("#VALUE!", "The pattern is too long (500 characters).");
  if (/^\(\?i\)/.test(p)) {
    p = p.slice(4);
    flags += "i";
  }
  if (/\\[1-9]|\\k<|\(\?<?[=!]/.test(p)) fail("#VALUE!", "This sheet's patterns, like Google Sheets', have no back references or look-arounds.");
  // a group that repeats something inside it, itself repeated: (a+)+, (x*y?){2,}; or a repeated choice of longer
  // parts, (a|ab)*. A repeated choice of single characters, (\d|,)+, is fine.
  const stack: { at: number; rep: boolean }[] = [];
  let reps = 0;
  const tooLong = "That pattern repeats a part that repeats itself, which can take too long. Write it more simply.";
  for (let i = 0; i < p.length; i++) {
    const ch = p[i];
    if (ch === "\\") {
      i++;
      continue;
    }
    if (ch === "[") {
      while (i < p.length && p[i] !== "]") i += p[i] === "\\" ? 2 : 1;
      continue;
    }
    if (ch === "(") {
      stack.push({ at: i, rep: false });
      // (?: and (?<name>: how the group is kept, not a repeat
      if (p[i + 1] === "?") {
        const m = /^\?(:|<[A-Za-z_][A-Za-z0-9_]*>)/.exec(p.slice(i + 1));
        if (m) i += m[0].length;
      }
    } else if (ch === ")") {
      const g = stack.pop();
      const next = p[i + 1];
      const repeated = next === "*" || next === "+" || (next === "{" && /^\{\d*,\d*\}|^\{\d+\}/.test(p.slice(i + 1)));
      if (g && repeated && (g.rep || longChoice(p.slice(g.at + 1, i)))) fail("#VALUE!", tooLong);
      if (stack.length && ((g?.rep ?? false) || repeated)) stack[stack.length - 1].rep = true;
    } else if (ch === "*" || ch === "+" || ch === "?" || ch === "{") {
      if (ch !== "?" || !/[*+?}]/.test(p[i - 1] ?? "")) reps++;
      if (stack.length) stack[stack.length - 1].rep = true;
    }
  }
  if (reps > 30) fail("#VALUE!", "That pattern repeats too many parts.");
  let re: RegExp;
  try {
    re = new RegExp(p, flags);
  } catch (e) {
    return fail("#VALUE!", `That pattern cannot be read: ${(e as Error).message.replace(/^Invalid regular expression: /, "")}.`);
  }
  const { run, anchored } = repeatRun(p, flags.replace("g", ""));
  return { re, run, anchored };
}

/** Letters a part of a pattern is tried on, to tell which parts can match the same letters. */
const SAMPLE = [...Array.from({ length: 95 }, (_, i) => String.fromCharCode(32 + i)), "\t", "\n", "\u00e9", "\u00a0", "\u20ac"];
const ALL = new Set(SAMPLE);

interface Part {
  /** the letters it can match; ALL for a group that may hold anything */
  set: Set<string>;
  /** may repeat without end: *, +, {n,} (or a long {n,m}) */
  open: boolean;
  /** may match nothing: ?, *, {0,…} */
  optional: boolean;
  /** matches no letter at all: ^ $ \b \B */
  zero?: boolean;
}

/**
 * A pattern's longest run of repeats that can take the same letters with nothing between them that only one could
 * take (a*a*a*: 3; .*a.*a.*: 3, since . takes a too; \s*\w+\s*: 1), and whether it starts with ^. Each branch of a |
 * is its own run; a group not repeated and with no | is read as its parts, any other group as one part that may
 * hold any letter.
 */
function repeatRun(p: string, flags: string): { run: number; anchored: boolean } {
  let best = 0;
  let anchoredAll = true;
  for (const branch of splitTop(p)) {
    const parts = partsOf(branch, flags);
    const anchored = parts[0]?.zero === true && branch.startsWith("^");
    if (!anchored) anchoredAll = false;
    let run = 0;
    let seen = new Set<string>();
    for (const x of parts) {
      if (x.zero) continue;
      const meets = run > 0 && [...x.set].some((ch) => seen.has(ch));
      if (x.open) {
        run = meets ? run + 1 : 1;
        seen = meets ? new Set([...seen, ...x.set]) : new Set(x.set);
        best = Math.max(best, run);
      } else if (!x.optional && !meets) {
        // a letter none of the run can take: the run ends there
        run = 0;
        seen = new Set();
      }
    }
  }
  return { run: best, anchored: anchoredAll };
}

/** A pattern split at its top-level |. */
function splitTop(p: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = "";
  for (let i = 0; i < p.length; i++) {
    const ch = p[i];
    if (ch === "\\") {
      cur += ch + (p[i + 1] ?? "");
      i++;
      continue;
    }
    if (ch === "[") {
      const j = classEnd(p, i);
      cur += p.slice(i, j + 1);
      i = j;
      continue;
    }
    if (ch === "(") depth++;
    if (ch === ")") depth--;
    if (ch === "|" && depth === 0) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

/** Where a [class] starting at i ends. */
function classEnd(p: string, i: number): number {
  let j = i + 1;
  if (p[j] === "^") j++;
  if (p[j] === "]") j++;
  while (j < p.length && p[j] !== "]") j += p[j] === "\\" ? 2 : 1;
  return Math.min(j, p.length - 1);
}

/** A branch's parts, in order, each with its repeat. */
function partsOf(p: string, flags: string): Part[] {
  const out: Part[] = [];
  for (let i = 0; i < p.length; i++) {
    const ch = p[i];
    let atom = "";
    let group: string | null = null;
    if (ch === "^" || ch === "$") {
      out.push({ set: new Set(), open: false, optional: true, zero: true });
      continue;
    }
    if (ch === "\\") {
      atom = p.slice(i, i + 2);
      i++;
      if (/^\\[bB]$/.test(atom)) {
        out.push({ set: new Set(), open: false, optional: true, zero: true });
        continue;
      }
    } else if (ch === "[") {
      const j = classEnd(p, i);
      atom = p.slice(i, j + 1);
      i = j;
    } else if (ch === "(") {
      let depth = 0;
      let j = i;
      for (; j < p.length; j++) {
        if (p[j] === "\\") {
          j++;
          continue;
        }
        if (p[j] === "[") {
          j = classEnd(p, j);
          continue;
        }
        if (p[j] === "(") depth++;
        if (p[j] === ")" && --depth === 0) break;
      }
      group = p.slice(i + 1, j).replace(/^\?(:|<[A-Za-z_][A-Za-z0-9_]*>)/, "");
      i = j;
    } else atom = ch;
    // its repeat
    let open = false;
    let optional = false;
    const q = /^(\*|\+|\?|\{(\d*)(,(\d*))?\})\??/.exec(p.slice(i + 1));
    if (q) {
      const t = q[1];
      if (t === "*") open = optional = true;
      else if (t === "+") open = true;
      else if (t === "?") optional = true;
      else {
        const lo = Number(q[2] || 0);
        const hi = q[3] === undefined ? lo : q[4] === "" || q[4] === undefined ? Infinity : Number(q[4]);
        open = hi - lo > 20;
        optional = lo === 0;
      }
      i += q[0].length;
    }
    if (group !== null) {
      const branches = splitTop(group);
      const inner = branches.length === 1 ? partsOf(group, flags) : [];
      // a group that is not repeated and holds no |: read as its own parts
      if (!q && branches.length === 1) {
        out.push(...inner);
        continue;
      }
      const set = branches.length === 1 ? new Set(inner.flatMap((x) => [...x.set])) : ALL;
      out.push({ set, open: open || inner.some((x) => x.open), optional });
      continue;
    }
    out.push({ set: lettersOf(atom, flags), open, optional });
  }
  return out;
}

/** The sample letters one part matches. */
function lettersOf(atom: string, flags: string): Set<string> {
  try {
    const re = new RegExp(`^(?:${atom})$`, flags.replace(/[gy]/g, ""));
    return new Set(SAMPLE.filter((ch) => re.test(ch)));
  } catch {
    return ALL;
  }
}

/** Whether a group's inside is a choice (|) with a part longer than one character or class. */
function longChoice(inside: string): boolean {
  const body = inside.replace(/^\?(:|<[A-Za-z_][A-Za-z0-9_]*>)/, "");
  // each part, with an escape or a [class] as one character; a part holding its own group counts as long
  const parts: string[] = [];
  let cur = "";
  let depth = 0;
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (ch === "\\") {
      cur += "x";
      i++;
    } else if (ch === "[") {
      while (i < body.length && body[i] !== "]") i += body[i] === "\\" ? 2 : 1;
      cur += "x";
    } else if (ch === "(") {
      depth++;
      cur += "((";
    } else if (ch === ")") {
      depth--;
    } else if (ch === "|" && depth === 0) {
      parts.push(cur);
      cur = "";
    } else cur += ch;
  }
  parts.push(cur);
  return parts.length > 1 && parts.some((x) => x.length > 1);
}

/** Whether text is a web address, as ISURL reads one: with or without https://, www. or not. */
function isUrl(s: string): boolean {
  const t = s.trim();
  if (/\s/.test(t) || !t) return false;
  if (/^(https?|ftp):\/\/[^\s/?#]+\.[^\s/?#]+/i.test(t)) return true;
  if (/^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(t)) return true;
  return /^(www\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}(:\d+)?([/?#][^\s]*)?$/i.test(t);
}

/**
 * Google Sheets' functions this sheet does not have, each with what to use instead: they reach other files, the web
 * or Google's own services. A formula that calls one shows #NAME? with these words.
 */
export const MISSING: Record<string, string> = {
  QUERY: "QUERY is a Google Sheets function this sheet does not have. FILTER, SORT, UNIQUE and SUMIFS do most of what it does.",
  IMPORTRANGE: "IMPORTRANGE is a Google Sheets function this sheet does not have: a formula here reads only this sheet's own tabs.",
  IMPORTXML: "IMPORTXML is a Google Sheets function this sheet does not have: formulas here never read the web. Ask your agent to fetch it.",
  IMPORTHTML: "IMPORTHTML is a Google Sheets function this sheet does not have: formulas here never read the web. Ask your agent to fetch it.",
  IMPORTDATA: "IMPORTDATA is a Google Sheets function this sheet does not have: formulas here never read the web. Import the file instead.",
  IMPORTFEED: "IMPORTFEED is a Google Sheets function this sheet does not have: formulas here never read the web. A board's feed widget reads one.",
  IMAGE: "IMAGE is a Google Sheets function this sheet does not have: a cell holds text, numbers and links.",
  SPARKLINE: "SPARKLINE is a Google Sheets function this sheet does not have. A chart widget, from this sheet, draws one.",
  GOOGLEFINANCE: "GOOGLEFINANCE is a Google Sheets function this sheet does not have: formulas here never read the web.",
  GOOGLETRANSLATE: "GOOGLETRANSLATE is a Google Sheets function this sheet does not have. Ask your agent to translate it.",
  DETECTLANGUAGE: "DETECTLANGUAGE is a Google Sheets function this sheet does not have. Ask your agent.",
};

/** A sort's order: numbers, then text, then TRUE and FALSE, then errors; empty cells last. */
export function sortCmp(x: Scalar, y: Scalar): number {
  const ex = x === null || x === "";
  const ey = y === null || y === "";
  if (ex || ey) return ex === ey ? 0 : ex ? 1 : -1;
  const xe = isErr(x);
  const ye = isErr(y);
  if (xe || ye) return xe === ye ? 0 : xe ? 1 : -1;
  return cmp(x, y);
}

/** The function names in order, for the formula box's suggestions. */
export const FUNC_NAMES = Object.keys(FUNCS).sort();
